import { useEffect, useState } from 'react';

import { Button, Flex, Select, Spin, Typography } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  LegendType,
  Line,
  ComposedChart,
} from 'recharts';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconCalender from '@/assets/icons/icon-calender.svg?react';
import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import IconSearch from '@/assets/icons/icon-search.svg?react';
import Card from '@/components/RCard';
import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import useCalculateEndDate, {
  EndDateFilters,
} from '@/hooks/useCalculateEndDate';
import {
  SimulationResult,
  SimulationResultFinancialActivity,
} from '@/types/simulations';
import { formatDateXAxis, getTickStep, tickFormatter } from '@/utils';

import StyledEbitda from './styles';
import { StyledSearch, StyledSelect, StyledDatePicker } from '../../styles';

interface EbitdaProps {
  activeTab?: string;
  simulationId?: number;
}

const Ebitda = ({ activeTab, simulationId }: EbitdaProps) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;

  const [storageSelection, setStorageSelection] = useState(
    activeTab || 'withStorage'
  );
  const [closedDataKeys, setClosedDataKeys] = useState<string[]>([]);
  const [selected, setSelected] = useState('Quickview');
  const [filters, setFilters] = useState<EndDateFilters>({
    startDate: '',
    endDate: '',
    timeUnit: 'year',
    inputValue: undefined,
  });
  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );
  const theme = useTheme();
  const isOnlyPurchaseEnergy = closedDataKeys.length === 4;

  const minRevenueValueMultiplier = isOnlyPurchaseEnergy ? 4 : 0.6;

  useEffect(() => {
    if (activeTab) {
      setStorageSelection(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    // Sadece gerekli olduğunda state güncellemesi yapın
    if (simulationData?.startDate && simulationData?.endDate) {
      setFilters((prevState) => ({
        ...prevState,
        startDate: moment(simulationData.startDate).toISOString(),
        endDate: moment(simulationData.endDate).toISOString(),
      }));
    }
  }, [simulationData?.startDate, simulationData?.endDate]);

  useCalculateEndDate(filters, setFilters);

  const handleStorageSelection = (value: string) => {
    setStorageSelection(value);
    setClosedDataKeys([]);
  };

  const shouldFetchData =
    filters.startDate && filters.endDate && filters.timeUnit;

  const {
    data: financialActivityData,
    isLoading,
    error,
  } = useSWR<SimulationResultFinancialActivity>(
    shouldFetchData
      ? `/simulation/${resultId}/financial_activity/?startDatetime=${filters.startDate}&endDatetime=${filters.endDate}&timeUnit=${filters.timeUnit}`
      : null,
    {
      revalidateOnFocus: false,
    }
  );
  const isDataZero = financialActivityData?.graphData.length === 0;

  const data =
    financialActivityData?.graphData.map((item) => {
      const filteredItem: {
        name: string;
        fromArbitrage?: number;
        fromPlant?: number;
        fromDischargedPlant?: number;
        ancillaryServices?: number;
        purchasedEnergy?: number;
        grossProfit?: number;
      } = {
        name: formatDateXAxis(item.dt, filters.timeUnit || 'year'),
      };

      switch (storageSelection) {
        case 'withStorage':
          if (
            !closedDataKeys.includes('fromArbitrage') &&
            item.revenueFromArbitrage !== undefined
          )
            filteredItem.fromArbitrage = item.revenueFromArbitrage;
          if (
            !closedDataKeys.includes('fromPlant') &&
            item.revenueFromPlant !== undefined
          )
            filteredItem.fromPlant = item.revenueFromPlant;
          if (
            !closedDataKeys.includes('fromDischargedPlant') &&
            item.revenueFromChargedEnergyFromPlant !== undefined
          )
            filteredItem.fromDischargedPlant =
              item.revenueFromChargedEnergyFromPlant;
          if (
            !closedDataKeys.includes('ancillaryServices') &&
            item.revenueFromAncillaryServices !== undefined
          )
            filteredItem.ancillaryServices = item.revenueFromAncillaryServices;
          if (
            !closedDataKeys.includes('purchasedEnergy') &&
            item.costOfPurchasedEnergy !== undefined
          )
            filteredItem.purchasedEnergy = item.costOfPurchasedEnergy;
          if (
            !closedDataKeys.includes('grossProfit') &&
            item.grossProfit !== undefined
          )
            filteredItem.grossProfit = item.grossProfit;
          break;

        case 'withoutStorage':
          if (
            !closedDataKeys.includes('fromPlant') &&
            item.revenueWos !== undefined
          )
            filteredItem.fromPlant = item.revenueWos;
          if (
            !closedDataKeys.includes('purchasedEnergy') &&
            item.cogsWos !== undefined
          )
            filteredItem.purchasedEnergy = item.cogsWos;
          if (
            !closedDataKeys.includes('grossProfit') &&
            item.grossProfit !== undefined
          )
            filteredItem.grossProfit = item.grossProfitWos;
          break;

        case 'storage':
          if (
            !closedDataKeys.includes('fromArbitrage') &&
            item.revenueFromArbitrage !== undefined
          )
            filteredItem.fromArbitrage = item.revenueFromArbitrage;
          if (
            !closedDataKeys.includes('fromDischargedPlant') &&
            item.revenueFromChargedEnergyFromPlant !== undefined
          )
            filteredItem.fromDischargedPlant =
              item.revenueFromChargedEnergyFromPlant;
          if (
            !closedDataKeys.includes('ancillaryServices') &&
            item.revenueFromAncillaryServices !== undefined
          )
            filteredItem.ancillaryServices = item.revenueFromAncillaryServices;
          if (
            !closedDataKeys.includes('purchasedEnergy') &&
            item.costOfPurchasedEnergyStorage !== undefined
          )
            filteredItem.purchasedEnergy = item.costOfPurchasedEnergyStorage;
          if (
            !closedDataKeys.includes('grossProfit') &&
            item.grossProfit !== undefined
          )
            filteredItem.grossProfit = item.grossProfitStorage;
          break;
      }

      return filteredItem;
    }) || [];

  const maxRevenueValue = Math.max(
    ...data.flatMap((item) => {
      const values = [];

      // Stack'lenmiş bar değerlerinin toplamı
      let stackedTotal = 0;
      if (item.fromArbitrage !== undefined)
        stackedTotal += Math.abs(item.fromArbitrage);
      if (item.fromPlant !== undefined)
        stackedTotal += Math.abs(item.fromPlant);
      if (item.fromDischargedPlant !== undefined)
        stackedTotal += Math.abs(item.fromDischargedPlant);
      if (item.ancillaryServices !== undefined)
        stackedTotal += Math.abs(item.ancillaryServices);
      if (item.purchasedEnergy !== undefined)
        stackedTotal += Math.abs(item.purchasedEnergy);

      if (stackedTotal !== 0) values.push(stackedTotal);

      // Gross Profit çizgisi
      if (item.grossProfit !== undefined && item.grossProfit !== 0) {
        values.push(Math.abs(item.grossProfit));
      }

      return values.length > 0 ? values : [0];
    })
  );

  const minRevenueValue = Math.min(
    ...data.flatMap((item) => {
      const values = [];

      // Stack'lenmiş bar değerlerinin toplamı
      let stackedTotal = 0;
      if (item.fromArbitrage !== undefined)
        stackedTotal += Math.abs(item.fromArbitrage);
      if (item.fromPlant !== undefined)
        stackedTotal += Math.abs(item.fromPlant);
      if (item.fromDischargedPlant !== undefined)
        stackedTotal += Math.abs(item.fromDischargedPlant);
      if (item.ancillaryServices !== undefined)
        stackedTotal += Math.abs(item.ancillaryServices);
      if (item.purchasedEnergy !== undefined)
        stackedTotal += Math.abs(item.purchasedEnergy);

      if (stackedTotal !== 0) values.push(stackedTotal);

      // Gross Profit çizgisi
      if (item.grossProfit !== undefined && item.grossProfit !== 0) {
        values.push(Math.abs(item.grossProfit));
      }

      return values.length > 0 ? values : [0];
    })
  );

  const roundToNearest = (num: number, nearest: number) => {
    return Math.round(num / nearest) * nearest;
  };

  const tickStep = getTickStep(
    Math.max(Math.abs(minRevenueValue), Math.abs(maxRevenueValue))
  );
  const upperBound = roundToNearest(maxRevenueValue * 0.6, tickStep);
  const lowerBound = roundToNearest(
    minRevenueValue * minRevenueValueMultiplier,
    tickStep
  );
  const maxAbsBound = Math.max(Math.abs(upperBound), Math.abs(lowerBound));
  const domain = !closedDataKeys.includes('purchasedEnergy')
    ? [-maxAbsBound, maxAbsBound]
    : [-maxAbsBound, 0];

  const step = Math.ceil(maxAbsBound / 2);

  const ticks =
    !closedDataKeys.includes('purchasedEnergy') && isOnlyPurchaseEnergy
      ? [-4 * step, -3 * step, -2 * step, -1 * step, 0, 1 * step, 2 * step]
          .map((value) => roundToNearest(value, tickStep))
          .filter(
            (value) =>
              value !== 0 || maxRevenueValue !== 0 || minRevenueValue !== 0
          )
          .filter((value, index, self) => self.indexOf(value) === index)
      : [-2 * step, -1 * step, 0, 1 * step, 2 * step, 3 * step, 4 * step]
          .map((value) => roundToNearest(value, tickStep))
          .filter(
            (value) =>
              value !== 0 || maxRevenueValue !== 0 || minRevenueValue !== 0
          )
          .filter((value, index, self) => self.indexOf(value) === index);
  const toggleClosedDataKeys = (key: string = '') => {
    if (closedDataKeys.includes(key)) {
      setClosedDataKeys(closedDataKeys.filter((item) => item !== key));
    } else {
      setClosedDataKeys([...closedDataKeys, key]);
    }
  };

  const handleViewChange = (view: string) => {
    // Index tabanlı yaklaşım - metin yerine index kullan
    const index = toggleLabels.indexOf(view);
    if (index === 0) {
      setSelected('Detailed');
    } else if (index === 1) {
      setSelected('Quickview');
    }
  };

  const handleTimeUnitChange = (value: unknown) => {
    setFilters({ ...filters, timeUnit: value as EndDateFilters['timeUnit'] });
  };

  const handleInputChange = (value: unknown) => {
    const inputValue = value as number;
    setFilters((prevState) => ({
      ...prevState,
      inputValue: inputValue <= 0 ? 1 : inputValue > 200 ? 200 : inputValue,
    }));
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return (
      current &&
      (current.isBefore(dayjs(simulationData?.startDate).startOf('day')) ||
        current.isAfter(dayjs(simulationData?.endDate).endOf('day')))
    );
  };

  const handleDatePickerChange = (date: unknown) => {
    const selectedDate = date as moment.Moment | null;
    if (selectedDate) {
      setFilters((prevState) => ({
        ...prevState,
        startDate: selectedDate
          ? selectedDate.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
          : '',
      }));
    }
  };

  // Toggle butonlar için index tabanlı yaklaşım
  const toggleLabels = [
    t('result.finance.ebitda.toggleButtons.detailed'),
    t('result.finance.ebitda.toggleButtons.quickview'),
  ];

  const getLegendPayload = () => {
    if (storageSelection === 'withoutStorage') {
      return [
        {
          value: t('result.finance.ebitda.legend.revenueFromPlant'),
          type: 'circle' as LegendType,
          id: 'fromPlant',
          color: theme.colors.purpleLight,
          inactive: closedDataKeys.includes('fromPlant'),
        },
        {
          value: t(
            'result.finance.ebitda.legend.costOfPurchasedEnergyConsumption'
          ),
          id: 'purchasedEnergy',
          type: 'circle' as LegendType,
          color: theme.colors.greenSecondary,
          inactive: closedDataKeys.includes('purchasedEnergy'),
        },
        {
          value: t('result.finance.ebitda.legend.grossProfit'),
          id: 'grossProfit',
          type: 'circle' as LegendType,
          color: theme.colors.bluePrimary,
          inactive: closedDataKeys.includes('grossProfit'),
        },
      ];
    } else if (storageSelection === 'storage') {
      return [
        {
          value: t('result.finance.ebitda.legend.revenueFromDischargedPlant'),
          type: 'circle' as LegendType,
          id: 'fromDischargedPlant',
          color: theme.colors.yellowPrimary,
          inactive: closedDataKeys.includes('fromDischargedPlant'),
        },
        {
          value: t('result.finance.ebitda.legend.revenueFromArbitrage'),
          type: 'circle' as LegendType,
          id: 'fromArbitrage',
          color: theme.colors.blueGray,
          inactive: closedDataKeys.includes('fromArbitrage'),
        },
        {
          value: t('result.finance.ebitda.legend.revenueFromAncillaryServices'),
          type: 'circle' as LegendType,
          id: 'ancillaryServices',
          color: theme.colors.blueLight,
          inactive: closedDataKeys.includes('ancillaryServices'),
        },
        {
          value: t('result.finance.ebitda.legend.costOfPurchasedEnergy'),
          id: 'purchasedEnergy',
          type: 'circle' as LegendType,
          color: theme.colors.greenSecondary,
          inactive: closedDataKeys.includes('purchasedEnergy'),
        },
        {
          value: t('result.finance.ebitda.legend.grossProfit'),
          id: 'grossProfit',
          type: 'circle' as LegendType,
          color: theme.colors.bluePrimary,
          inactive: closedDataKeys.includes('grossProfit'),
        },
      ];
    } else {
      return [
        {
          value: t('result.finance.ebitda.legend.revenueFromPlant'),
          type: 'circle' as LegendType,
          id: 'fromPlant',
          color: theme.colors.purpleLight,
          inactive: closedDataKeys.includes('fromPlant'),
        },
        {
          value: t('result.finance.ebitda.legend.revenueFromDischargedPlant'),
          type: 'circle' as LegendType,
          id: 'fromDischargedPlant',
          color: theme.colors.yellowPrimary,
          inactive: closedDataKeys.includes('fromDischargedPlant'),
        },
        {
          value: t('result.finance.ebitda.legend.revenueFromArbitrage'),
          type: 'circle' as LegendType,
          id: 'fromArbitrage',
          color: theme.colors.blueGray,
          inactive: closedDataKeys.includes('fromArbitrage'),
        },
        {
          value: t('result.finance.ebitda.legend.revenueFromAncillaryServices'),
          type: 'circle' as LegendType,
          id: 'ancillaryServices',
          color: theme.colors.blueLight,
          inactive: closedDataKeys.includes('ancillaryServices'),
        },
        {
          value: t('result.finance.ebitda.legend.costOfPurchasedEnergy'),
          id: 'purchasedEnergy',
          type: 'circle' as LegendType,
          color: theme.colors.greenSecondary,
          inactive: closedDataKeys.includes('purchasedEnergy'),
        },
        {
          value: t('result.finance.ebitda.legend.grossProfit'),
          id: 'grossProfit',
          type: 'circle' as LegendType,
          color: theme.colors.bluePrimary,
          inactive: closedDataKeys.includes('grossProfit'),
        },
      ];
    }
  };

  return (
    <StyledEbitda>
      <Card
        title={
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.ebitda.title')}
            </Typography.Title>
            <RTooltip
              title={t('result.finance.ebitda.title')}
              description={t('result.finance.ebitda.tooltip')}
            >
              <IconInfo />
            </RTooltip>
          </Flex>
        }
        extra={
          <Flex gap={16} align="center">
            <Flex gap={8} align="center">
              {selected === 'Detailed' && (
                <>
                  <motion.div
                    whileTap={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  >
                    <StyledDatePicker
                      onChange={handleDatePickerChange}
                      placeholder={t(
                        'result.finance.ebitda.datePicker.placeholder'
                      )}
                      suffixIcon={<IconCalender />}
                      disabledDate={disabledDate}
                    />
                  </motion.div>
                  <motion.div
                    whileTap={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  >
                    <StyledSelect
                      defaultValue={t(
                        'result.finance.ebitda.timeUnit.defaultValue'
                      )}
                      style={{ width: '109px' }}
                      onChange={handleTimeUnitChange}
                    >
                      <StyledSelect.Option value="year">
                        {t('result.finance.ebitda.timeUnit.year')}
                      </StyledSelect.Option>
                      <StyledSelect.Option value="month">
                        {t('result.finance.ebitda.timeUnit.month')}
                      </StyledSelect.Option>
                      <StyledSelect.Option value="week">
                        {t('result.finance.ebitda.timeUnit.week')}
                      </StyledSelect.Option>
                      <StyledSelect.Option value="day">
                        {t('result.finance.ebitda.timeUnit.day')}
                      </StyledSelect.Option>
                      <StyledSelect.Option value="hour">
                        {t('result.finance.ebitda.timeUnit.hour')}
                      </StyledSelect.Option>
                    </StyledSelect>
                  </motion.div>
                  <motion.div
                    whileTap={{ scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 24 }}
                  >
                    <StyledSearch
                      placeholder={t(
                        'result.finance.ebitda.search.placeholder'
                      )}
                      onChange={handleInputChange}
                      prefix={
                        <IconSearch style={{ height: '16px', width: 'auto' }} />
                      }
                      type="number"
                      value={filters.inputValue}
                      status={
                        filters.inputValue !== undefined &&
                        (filters.inputValue === 1 || filters.inputValue === 200)
                          ? 'warning'
                          : undefined
                      }
                    />
                  </motion.div>
                </>
              )}
              <motion.div
                whileTap={{ scale: 1.1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 24 }}
              >
                <Select
                  placeholder={t(
                    'result.finance.ebitda.storageSelection.placeholder'
                  )}
                  style={{ minWidth: '150px', width: '150px' }}
                  value={storageSelection}
                  onChange={handleStorageSelection}
                >
                  <Select.Option value="withStorage">
                    {t('result.finance.ebitda.storageSelection.withStorage')}
                  </Select.Option>
                  <Select.Option value="withoutStorage">
                    {t('result.finance.ebitda.storageSelection.withoutStorage')}
                  </Select.Option>
                  <Select.Option value="storage">
                    {t('result.finance.ebitda.storageSelection.storage')}
                  </Select.Option>
                </Select>
              </motion.div>
              <RToggleButtons
                labels={toggleLabels}
                onLabelChange={handleViewChange}
                initialActiveIndex={selected === 'Detailed' ? 0 : 1}
              />
            </Flex>
            <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
          </Flex>
        }
        $paddingBody="24px"
        styles={{ header: { padding: '14px 24px' } }}
        style={{ width: '100%', marginTop: '24px' }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" style={{ height: 400 }}>
            <Spin style={{ margin: '20px' }} />
          </Flex>
        ) : error || isDataZero ? (
          <Flex
            vertical
            gap={12}
            justify="center"
            align="center"
            style={{ height: 400 }}
          >
            <IconError width={50} height={50} fill={theme.colors.tagFailure} />
            {error && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.error.fetchDataError')}
              </Typography.Text>
            )}
            {isDataZero && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.finance.cashFlow.noDataAvailable')}
              </Typography.Text>
            )}
          </Flex>
        ) : (
          <ResponsiveContainer width="100%" height={415}>
            <ComposedChart data={data} stackOffset="sign">
              <CartesianGrid vertical={false} horizontal={true} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} />
              <YAxis
                domain={domain}
                tickFormatter={(value) => tickFormatter(value, currencySymbol)}
                ticks={ticks}
                tick={{ dx: -9 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomGraphicTooltip />} />
              <Legend
                payload={getLegendPayload()}
                onClick={({ id }) => toggleClosedDataKeys(id)}
                iconSize={10}
              />

              {!closedDataKeys.includes('purchasedEnergy') && (
                <Bar
                  dataKey="purchasedEnergy"
                  name={t('result.finance.ebitda.legend.costOfPurchasedEnergy')}
                  stackId="a"
                  fill={theme.colors.greenSecondary}
                  barSize={50}
                  unit={currencySymbol}
                />
              )}
              {!closedDataKeys.includes('fromPlant') && (
                <Bar
                  dataKey="fromPlant"
                  name={t('result.finance.ebitda.legend.revenueFromPlant')}
                  stackId="a"
                  fill={theme.colors.purpleLight}
                  barSize={50}
                  unit={currencySymbol}
                />
              )}
              {!closedDataKeys.includes('grossProfit') && (
                <Line
                  type="linear"
                  dataKey="grossProfit"
                  name={t('result.finance.ebitda.legend.grossProfit')}
                  stroke={theme.colors.bluePrimary}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  unit={currencySymbol}
                />
              )}
              {storageSelection !== 'withoutStorage' && (
                <>
                  {!closedDataKeys.includes('ancillaryServices') && (
                    <Bar
                      dataKey="ancillaryServices"
                      name={t(
                        'result.finance.ebitda.legend.revenueFromAncillaryServices'
                      )}
                      stackId="a"
                      fill={theme.colors.blueLight}
                      barSize={50}
                      unit={currencySymbol}
                    />
                  )}
                  {!closedDataKeys.includes('fromArbitrage') && (
                    <Bar
                      dataKey="fromArbitrage"
                      name={t(
                        'result.finance.ebitda.legend.revenueFromArbitrage'
                      )}
                      stackId="a"
                      fill={theme.colors.blueGray}
                      barSize={50}
                      unit={currencySymbol}
                    />
                  )}
                  {!closedDataKeys.includes('fromDischargedPlant') && (
                    <Bar
                      dataKey="fromDischargedPlant"
                      name={t(
                        'result.finance.ebitda.legend.revenueFromDischargedPlant'
                      )}
                      stackId="a"
                      fill={theme.colors.yellowPrimary}
                      barSize={50}
                      unit={currencySymbol}
                    />
                  )}
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>
    </StyledEbitda>
  );
};
export default Ebitda;
